(function () {
  if (customElements.get('slider-component')) return;

  function queryAll(root, selector) {
    return Array.from(root.querySelectorAll(selector));
  }

  function debounce(fn, wait) {
    let timeout;
    const debounced = (...args) => {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => fn.apply(null, args), wait);
    };
    debounced.cancel = () => window.clearTimeout(timeout);
    return debounced;
  }

  class SliderComponent extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) return;

      this.ul = this.querySelector('ul[id^="Slider-"]');
      if (!this.ul) return;

      this.isInitialized = true;
      this.cleanupCallbacks = [];
      this.prevBtn = this.querySelector('.slider-button--prev');
      this.nextBtn = this.querySelector('.slider-button--next');
      this.currentEl = this.querySelector('.slider-counter--current');
      this.totalEl = this.querySelector('.slider-counter--total');
      this.slides = queryAll(this.ul, '.slider__slide, li');
      this.total = this.slides.length;

      if (this.totalEl) this.totalEl.textContent = this.total;
      if (this.currentEl) this.currentEl.textContent = this.total > 0 ? '1' : '0';

      this.onPrev = (event) => {
        event?.preventDefault();
        this.scrollByStep(-1);
      };
      this.onNext = (event) => {
        event?.preventDefault();
        this.scrollByStep(1);
      };
      this.debouncedUpdateState = debounce(() => this.updateState(), 80);
      this.onScroll = () => {
        if (this.programmaticScroll) {
          window.clearTimeout(this.programmaticScrollTimer);
      window.clearTimeout(this.programmaticScrollFallbackTimer);
          this.programmaticScrollTimer = window.setTimeout(() => {
            this.programmaticScroll = false;
            this.lockedIndex = null;
            this.updateState();
          }, 140);
          return;
        }

        this.debouncedUpdateState();
      };
      this.onResize = debounce(() => this.refresh(true), 120);

      this.addManagedListener(this.prevBtn, 'click', this.onPrev);
      this.addManagedListener(this.nextBtn, 'click', this.onNext);
      this.addManagedListener(this.ul, 'scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      this.cleanupCallbacks.push(() => window.removeEventListener('resize', this.onResize));

      this.bindDrag();
      this.refresh(true);
    }

    disconnectedCallback() {
      this.cleanupCallbacks?.forEach((cleanup) => cleanup());
      this.cleanupCallbacks = [];
      this.debouncedUpdateState?.cancel?.();
      this.onResize?.cancel?.();
      window.clearTimeout(this.programmaticScrollTimer);
      window.clearTimeout(this.programmaticScrollFallbackTimer);

      if (this.ul) {
        this.ul.classList.remove('is-dragging');
        this.ul.style.cursor = '';
        this.ul.style.userSelect = '';
        this.ul.style.scrollBehavior = '';
        this.ul.style.scrollSnapType = '';
      }

      this.isInitialized = false;
    }

    addManagedListener(target, type, listener, options) {
      if (!target) return;
      target.addEventListener(type, listener, options);
      this.cleanupCallbacks.push(() => target.removeEventListener(type, listener, options));
    }

    refresh(forceTotal) {
      this.slides = queryAll(this.ul, '.slider__slide, li');
      this.total = this.slides.length;
      if (forceTotal && this.totalEl) this.totalEl.textContent = this.total;
      this.toggleControls();
      this.updateState();
    }

    toggleControls() {
      const controls = this.querySelector('.slider-buttons');
      if (!controls) return;

      const shouldHide = this.total <= 1 || this.ul.scrollWidth <= this.ul.clientWidth + 2;
      controls.hidden = shouldHide;
      controls.setAttribute('aria-hidden', shouldHide ? 'true' : 'false');
    }

    paddingInlineStart() {
      const styles = getComputedStyle(this.ul);
      return parseFloat(styles.paddingInlineStart || styles.paddingLeft || '0') || 0;
    }

    scrollStart() {
      const maxScroll = Math.max(0, this.ul.scrollWidth - this.ul.clientWidth);
      const raw = this.ul.scrollLeft;

      if (getComputedStyle(this.ul).direction !== 'rtl') {
        return Math.max(0, raw);
      }

      if (raw < 0) return Math.min(maxScroll, Math.abs(raw));
      return Math.max(0, maxScroll - raw);
    }

    scrollLeftForStart(start) {
      const maxScroll = Math.max(0, this.ul.scrollWidth - this.ul.clientWidth);
      const clampedStart = Math.max(0, Math.min(start, maxScroll));

      if (getComputedStyle(this.ul).direction !== 'rtl') {
        return clampedStart;
      }

      if (this.ul.scrollLeft < 0) return -clampedStart;
      return maxScroll - clampedStart;
    }

    visibleIndex() {
      const start = this.scrollStart();
      if (start <= 2) return 0;

      const target = start + this.paddingInlineStart();
      let bestIndex = 0;
      let bestDistance = Number.POSITIVE_INFINITY;

      this.slides.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetLeft - target);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestIndex = index;
        }
      });

      return Math.max(0, Math.min(bestIndex, Math.max(0, this.total - 1)));
    }

    scrollByStep(direction) {
      if (!this.total) return;

      const currentIndex = this.visibleIndex();
      const nextIndex = Math.max(0, Math.min(currentIndex + direction, this.total - 1));
      const nextSlide = this.slides[nextIndex];
      if (!nextSlide) return;

      const target = Math.max(0, nextSlide.offsetLeft - this.paddingInlineStart());
      this.programmaticScroll = true;
      this.lockedIndex = nextIndex;
      this.updateState(nextIndex);
      this.ul.scrollTo({ left: this.scrollLeftForStart(target), behavior: 'smooth' });
      window.clearTimeout(this.programmaticScrollFallbackTimer);
      this.programmaticScrollFallbackTimer = window.setTimeout(() => {
        this.programmaticScroll = false;
        this.lockedIndex = null;
        this.updateState();
      }, 700);
    }

    updateState(forcedIndex = null) {
      const index = Number.isInteger(forcedIndex) ? forcedIndex : this.lockedIndex ?? this.visibleIndex();
      if (this.currentEl) this.currentEl.textContent = String(this.total ? index + 1 : 0);

      const start = this.scrollStart();
      const maxScroll = Math.max(0, this.ul.scrollWidth - this.ul.clientWidth);
      const atStart = start <= 2;
      const atEnd = maxScroll - start <= 2;

      if (this.programmaticScroll || Number.isInteger(forcedIndex)) {
        if (this.prevBtn) this.prevBtn.disabled = index <= 0 || this.total <= 1;
        if (this.nextBtn) this.nextBtn.disabled = index >= this.total - 1 || this.total <= 1;
        return;
      }

      if (this.prevBtn) this.prevBtn.disabled = atStart || this.total <= 1;
      if (this.nextBtn) this.nextBtn.disabled = atEnd || this.total <= 1;
    }

    bindDrag() {
      if (window.matchMedia('(max-width: 989px)').matches) return;
      const ul = this.ul;
      if (!ul) return;

      const interactiveSelector = [
        'a',
        'button',
        'input',
        'select',
        'textarea',
        'label',
        'summary',
        'details',
        'product-form',
        'modal-opener',
        'quick-add-bulk',
        'quick-add-modal',
        '.quick-add',
        '.quantity-popover',
        '[data-rp-wishlist]',
      ].join(',');
      const hasProductCards = ul.classList.contains('product-grid') || !!ul.querySelector('.product-card-wrapper');
      if (hasProductCards) return;

      let isPointerDown = false;
      let startX = 0;
      let startScrollLeft = 0;
      let hasDragged = false;

      ul.style.cursor = 'grab';
      ul.style.userSelect = 'none';

      const onPointerDown = (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (event.target.closest(interactiveSelector)) return;

        isPointerDown = true;
        hasDragged = false;
        startX = event.clientX;
        startScrollLeft = ul.scrollLeft;

        ul.classList.add('is-dragging');
        ul.style.cursor = 'grabbing';
        ul.style.scrollBehavior = 'auto';
        ul.style.scrollSnapType = 'none';

        try {
          ul.setPointerCapture(event.pointerId);
        } catch (_) {}
      };

      const onPointerMove = (event) => {
        if (!isPointerDown) return;
        const dx = event.clientX - startX;
        if (Math.abs(dx) > 4) hasDragged = true;
        ul.scrollLeft = startScrollLeft - dx;
        if (hasDragged) event.preventDefault();
      };

      const stopDrag = (event) => {
        if (!isPointerDown) return;
        isPointerDown = false;
        ul.classList.remove('is-dragging');
        ul.style.cursor = 'grab';
        ul.style.scrollBehavior = '';
        ul.style.scrollSnapType = '';

        try {
          ul.releasePointerCapture(event.pointerId);
        } catch (_) {}

        this.updateState();
      };

      const onClickCapture = (event) => {
        if (!hasDragged) return;
        event.preventDefault();
        event.stopPropagation();
        hasDragged = false;
      };

      this.addManagedListener(ul, 'pointerdown', onPointerDown, { passive: true });
      this.addManagedListener(ul, 'pointermove', onPointerMove, { passive: false });
      this.addManagedListener(ul, 'pointerup', stopDrag);
      this.addManagedListener(ul, 'pointercancel', stopDrag);
      this.addManagedListener(ul, 'lostpointercapture', stopDrag);
      this.addManagedListener(ul, 'click', onClickCapture, true);
    }
  }

  customElements.define('slider-component', SliderComponent);
})();
